import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
  getImpiantoSettings, updateImpiantoSettings,
  type ImpiantoSettings, type TipoDocumentoVendita,
} from '../../api/impianto_settings';
import { useAuthStore } from '../../store/authStore';
import { canWriteSettings } from '../../lib/constants';

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white disabled:bg-stone-bg disabled:text-ink-soft disabled:cursor-not-allowed disabled:border-stone-mid';
const labelCls = 'block text-xs font-medium text-ink-soft mb-1';

// Liste ufficiali FatturaPA (SdI).
const REGIMI = ['RF01', 'RF02', 'RF04', 'RF05', 'RF06', 'RF07', 'RF08', 'RF09', 'RF10', 'RF11', 'RF12', 'RF13', 'RF14', 'RF15', 'RF16', 'RF17', 'RF18', 'RF19'];
const MODALITA = Array.from({ length: 23 }, (_, i) => `MP${String(i + 1).padStart(2, '0')}`);

// Campi editabili nella sezione (sottoinsieme FatturaPA del cedente).
interface Form {
  regime_fiscale: string;
  condizioni_pagamento_default: string;
  modalita_pagamento_default: string;
  iva_trasporto_default: string;
  stato_liquidazione: string;
  socio_unico: string;   // 'SU' | 'SM' (mappa su boolean)
  sede_nazione: string;
  idpaese_trasmittente: string;
  codice_trasmittente: string;
  iban: string;
  banca: string;
  abi: string;
  cab: string;
  rea_ufficio: string;
  rea_numero: string;
  capitale_sociale: string;
  pec_cedente: string;
  numero_fattura_seed: string;
  sezionale_fattura: string;
  // V72 — scadenza e tipo documento della vendita.
  giorni_scadenza_default: string;
  tipo_documento_vendita: TipoDocumentoVendita;
  // V69 — ricezione fatture passive (sezione dedicata, concetti diversi dal cedente).
  codice_destinatario_ricezione: string;
  pec_ricezione: string;
}

const EMPTY: Form = {
  regime_fiscale: 'RF01', condizioni_pagamento_default: 'TP02', modalita_pagamento_default: 'MP05',
  iva_trasporto_default: '10',
  stato_liquidazione: 'LN', socio_unico: 'SM', sede_nazione: 'IT', idpaese_trasmittente: 'IT',
  codice_trasmittente: '', iban: '', banca: '', abi: '', cab: '', rea_ufficio: '', rea_numero: '',
  capitale_sociale: '', pec_cedente: '', numero_fattura_seed: '0', sezionale_fattura: 'M',
  giorni_scadenza_default: '30', tipo_documento_vendita: 'TD01',
  codice_destinatario_ricezione: '', pec_ricezione: '',
};

function toForm(s: ImpiantoSettings): Form {
  return {
    regime_fiscale: s.regime_fiscale ?? 'RF01',
    condizioni_pagamento_default: s.condizioni_pagamento_default ?? 'TP02',
    modalita_pagamento_default: s.modalita_pagamento_default ?? 'MP05',
    iva_trasporto_default: s.iva_trasporto_default != null ? String(s.iva_trasporto_default) : '10',
    stato_liquidazione: s.stato_liquidazione ?? 'LN',
    socio_unico: s.socio_unico ? 'SU' : 'SM',
    sede_nazione: s.sede_nazione ?? 'IT',
    idpaese_trasmittente: s.idpaese_trasmittente ?? 'IT',
    codice_trasmittente: s.codice_trasmittente ?? '',
    iban: s.iban ?? '',
    banca: s.banca ?? '',
    abi: s.abi ?? '',
    cab: s.cab ?? '',
    rea_ufficio: s.rea_ufficio ?? '',
    rea_numero: s.rea_numero ?? '',
    capitale_sociale: s.capitale_sociale != null ? String(s.capitale_sociale) : '',
    pec_cedente: s.pec_cedente ?? '',
    numero_fattura_seed: s.numero_fattura_seed != null ? String(s.numero_fattura_seed) : '0',
    sezionale_fattura: s.sezionale_fattura ?? 'M',
    giorni_scadenza_default: s.giorni_scadenza_default != null ? String(s.giorni_scadenza_default) : '30',
    tipo_documento_vendita: s.tipo_documento_vendita ?? 'TD01',
    codice_destinatario_ricezione: s.codice_destinatario_ricezione ?? '',
    pec_ricezione: s.pec_ricezione ?? '',
  };
}

export default function DatiFatturazioneSettings() {
  const { t } = useTranslation();
  // Scrittura Settings = {admin, DIR, RT}; AMM sola lettura.
  const role = useAuthStore((s) => s.user?.role);
  const canWrite = canWriteSettings(role);
  // Campi FISCALI → solo {admin, DIR} (RT escluso su QUESTI campi): aliquota
  // trasporto, sezionale e tipo documento di vendita. Specchia il gate
  // field-level del backend (`_CAMPI_FISCALI` in impianto_settings.py): il 403
  // c'e', e il FE non deve farlo comparire come errore grezzo.
  const canEditFiscal = role === 'admin' || role === 'DIR';
  const [form, setForm] = useState<Form>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState(false);

  useEffect(() => {
    getImpiantoSettings()
      .then((s) => setForm(toForm(s)))
      .catch(() => setError(t('settings.dati_fatt.errore_caricamento')))
      .finally(() => setLoading(false));
  }, [t]);

  // Il banner "salvato" si spegne da solo dopo 3s. Il timer sta QUI e non nel
  // gestore di `save()`: li' era un `setTimeout` senza `clearTimeout`, e dopo
  // lo smontaggio scattava lo stesso chiamando `setSaveOk` su un componente
  // che non c'e' piu'. Nei test vitest usciva con 1 ANCHE a test tutti verdi,
  // in modo dipendente dal carico — il rosso in CI che non si riproduceva in
  // locale. Stessa forma del toast di navigazione in DettaglioRichiestaPage.
  useEffect(() => {
    if (!saveOk) return;
    const timer = setTimeout(() => setSaveOk(false), 3000);
    return () => clearTimeout(timer);
  }, [saveOk]);

  function set<K extends keyof Form>(key: K, value: Form[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function save() {
    const seed = Number(form.numero_fattura_seed);
    if (Number.isNaN(seed) || seed < 0 || !Number.isInteger(seed)) {
      setError(t('settings.dati_fatt.val_seed'));
      return;
    }
    const capitale = form.capitale_sociale.trim() === '' ? undefined : Number(form.capitale_sociale);
    if (capitale != null && (Number.isNaN(capitale) || capitale < 0)) {
      setError(t('settings.dati_fatt.val_capitale'));
      return;
    }
    // L'aliquota trasporto va nel payload solo se l'utente può editarla ({admin,DIR}):
    // includerla da RT darebbe 403 sull'intero salvataggio (gate field-level BE).
    let ivaTrasp: number | undefined;
    if (canEditFiscal) {
      ivaTrasp = Number(form.iva_trasporto_default);
      if (Number.isNaN(ivaTrasp) || ivaTrasp <= 0 || ivaTrasp > 100) {
        setError(t('settings.dati_fatt.val_iva_trasporto'));
        return;
      }
    }
    // Sezionale fattura: stesso gate field-level {admin,DIR}. Vuoto = numerazione
    // legacy senza suffisso, quindi '' è un valore valido da inviare.
    let sezionale: string | undefined;
    if (canEditFiscal) {
      sezionale = form.sezionale_fattura.trim();
      if (/\s/.test(sezionale) || sezionale.length > 10) {
        setError(t('settings.dati_fatt.val_sezionale'));
        return;
      }
    }
    // V72: giorni di scadenza. Intero >= 0 (0 = pagamento a vista, che e' un
    // valore legittimo); il BE accetta fino a 365.
    const giorni = Number(form.giorni_scadenza_default);
    if (Number.isNaN(giorni) || giorni < 0 || giorni > 365 || !Number.isInteger(giorni)) {
      setError(t('settings.dati_fatt.val_giorni_scadenza'));
      return;
    }
    setSaving(true); setError(null); setSaveOk(false);
    try {
      const updated = await updateImpiantoSettings({
        regime_fiscale: form.regime_fiscale,
        condizioni_pagamento_default: form.condizioni_pagamento_default,
        modalita_pagamento_default: form.modalita_pagamento_default,
        ...(ivaTrasp != null ? { iva_trasporto_default: String(ivaTrasp) } : {}),
        stato_liquidazione: form.stato_liquidazione,
        socio_unico: form.socio_unico === 'SU',
        sede_nazione: form.sede_nazione || undefined,
        idpaese_trasmittente: form.idpaese_trasmittente || undefined,
        codice_trasmittente: form.codice_trasmittente || undefined,
        iban: form.iban || undefined,
        banca: form.banca || undefined,
        abi: form.abi || undefined,
        cab: form.cab || undefined,
        rea_ufficio: form.rea_ufficio || undefined,
        rea_numero: form.rea_numero || undefined,
        capitale_sociale: capitale,
        pec_cedente: form.pec_cedente || undefined,
        numero_fattura_seed: seed,
        ...(sezionale != null ? { sezionale_fattura: sezionale } : {}),
        // `giorni_scadenza_default` resta libero: i termini di pagamento sono una
        // condizione commerciale. `tipo_documento_vendita` (TD01/TD24) e' invece
        // la forma fiscale del documento e il BE risponde 403 a chi non e'
        // {admin, DIR} → omesso dal payload, come sezionale e iva_trasporto.
        giorni_scadenza_default: giorni,
        ...(canEditFiscal ? { tipo_documento_vendita: form.tipo_documento_vendita } : {}),
        // V69: stesso pattern `|| undefined` degli altri campi (vuoto = invariato).
        codice_destinatario_ricezione: form.codice_destinatario_ricezione || undefined,
        pec_ricezione: form.pec_ricezione || undefined,
      });
      setForm(toForm(updated));
      setSaveOk(true);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ?? t('settings.dati_fatt.errore_salvataggio'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-center py-8 text-sm text-ink-soft">{t('settings.dati_fatt.caricamento')}</div>;

  return (
    <div className="space-y-5 bg-white border border-stone-mid rounded-xl p-5">
      <p className="text-sm text-ink-mid">
        {t('settings.dati_fatt.intro')}
      </p>

      {error && <div className="p-3 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">{error}</div>}
      {saveOk && <div data-testid="fatturazione-save-ok" className="p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">{t('settings.dati_fatt.salvato_ok')}</div>}

      {/* Sola lettura visiva per chi non può scrivere (AMM): fieldset disabled
          → tutti i controlli disabilitati e stilizzati come non editabili. */}
      <fieldset disabled={!canWrite} className="space-y-5 m-0 p-0 border-0 min-w-0">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.regime_fiscale')}</label>
          <select className={inputCls} data-testid="regime-fiscale" value={form.regime_fiscale} onChange={(e) => set('regime_fiscale', e.target.value)}>
            {REGIMI.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.stato_liquidazione')}</label>
          <select className={inputCls} value={form.stato_liquidazione} onChange={(e) => set('stato_liquidazione', e.target.value)}>
            <option value="LN">{t('settings.dati_fatt.liq_ln')}</option>
            <option value="LS">{t('settings.dati_fatt.liq_ls')}</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.condizioni_pagamento')}</label>
          <select className={inputCls} value={form.condizioni_pagamento_default} onChange={(e) => set('condizioni_pagamento_default', e.target.value)}>
            <option value="TP01">{t('settings.dati_fatt.tp01')}</option>
            <option value="TP02">{t('settings.dati_fatt.tp02')}</option>
            <option value="TP03">{t('settings.dati_fatt.tp03')}</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.modalita_pagamento')}</label>
          <select className={inputCls} value={form.modalita_pagamento_default} onChange={(e) => set('modalita_pagamento_default', e.target.value)}>
            {MODALITA.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.iva_trasporto')}</label>
          <input type="number" step="0.01" min="0" max="100" className={inputCls} disabled={!canEditFiscal}
            data-testid="iva-trasporto-default" value={form.iva_trasporto_default}
            onChange={(e) => set('iva_trasporto_default', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.socio_unico')}</label>
          <select className={inputCls} value={form.socio_unico} onChange={(e) => set('socio_unico', e.target.value)}>
            <option value="SM">{t('settings.dati_fatt.socio_sm')}</option>
            <option value="SU">{t('settings.dati_fatt.socio_su')}</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.nazione_sede')}</label>
          <input type="text" className={inputCls} value={form.sede_nazione} onChange={(e) => set('sede_nazione', e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-stone-mid/60">
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.idpaese')}</label>
          <input type="text" className={inputCls} value={form.idpaese_trasmittente} onChange={(e) => set('idpaese_trasmittente', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.codice_trasm')}</label>
          <input type="text" className={inputCls} value={form.codice_trasmittente} onChange={(e) => set('codice_trasmittente', e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className={labelCls}>{t('settings.dati_fatt.iban')}</label>
          <input type="text" className={inputCls} value={form.iban} onChange={(e) => set('iban', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className={labelCls}>{t('settings.dati_fatt.banca')}</label>
          <input type="text" className={inputCls} value={form.banca} onChange={(e) => set('banca', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.abi')}</label>
          <input type="text" className={inputCls} value={form.abi} onChange={(e) => set('abi', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.cab')}</label>
          <input type="text" className={inputCls} value={form.cab} onChange={(e) => set('cab', e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.rea_ufficio')}</label>
          <input type="text" className={inputCls} value={form.rea_ufficio} onChange={(e) => set('rea_ufficio', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.rea_numero')}</label>
          <input type="text" className={inputCls} value={form.rea_numero} onChange={(e) => set('rea_numero', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.capitale')}</label>
          <input type="number" step="0.01" min="0" className={inputCls} value={form.capitale_sociale} onChange={(e) => set('capitale_sociale', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.pec')}</label>
          <input type="text" className={inputCls} value={form.pec_cedente} onChange={(e) => set('pec_cedente', e.target.value)} />
        </div>
      </div>

      <div className="pt-2 border-t border-stone-mid/60 grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.seed')}</label>
          <input
            type="number" min="0" step="1" className={`${inputCls} max-w-[200px]`}
            data-testid="numero-fattura-seed"
            value={form.numero_fattura_seed}
            onChange={(e) => set('numero_fattura_seed', e.target.value)}
          />
          <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
            <AlertTriangle size={13} /> {t('settings.dati_fatt.seed_warning')}
          </p>
        </div>
        <div>
          <label className={labelCls}>{t('settings.dati_fatt.sezionale')}</label>
          <input
            type="text" maxLength={10} className={`${inputCls} max-w-[200px]`} disabled={!canEditFiscal}
            data-testid="sezionale-fattura"
            value={form.sezionale_fattura}
            onChange={(e) => set('sezionale_fattura', e.target.value)}
          />
          <p className="text-xs text-ink-soft mt-1">{t('settings.dati_fatt.sezionale_hint')}</p>
        </div>
      </div>

      {/* V72 — Scadenza e tipo documento. `giorni_scadenza_default` vale su TUTTE
          le attive, servizio compreso: non e' un parametro della sola vendita,
          e l'help text lo dice perche' il titolo della sezione la nomina. */}
      <div className="pt-3 border-t border-stone-mid/60">
        <h3 className="text-sm font-semibold text-ink-dark">{t('settings.dati_fatt.vendita_titolo')}</h3>
        <p className="text-xs text-ink-soft mt-1 mb-3">{t('settings.dati_fatt.vendita_help')}</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>{t('settings.dati_fatt.giorni_scadenza')}</label>
            <input
              type="number" min="0" max="365" step="1" className={`${inputCls} max-w-[200px]`}
              data-testid="giorni-scadenza-default"
              value={form.giorni_scadenza_default}
              onChange={(e) => set('giorni_scadenza_default', e.target.value)}
            />
            <p className="text-xs text-ink-soft mt-1">{t('settings.dati_fatt.giorni_scadenza_hint')}</p>
          </div>
          <div>
            <label className={labelCls}>{t('settings.dati_fatt.tipo_documento_vendita')}</label>
            <select
              className={`${inputCls} max-w-[200px]`} data-testid="tipo-documento-vendita"
              disabled={!canEditFiscal}
              value={form.tipo_documento_vendita}
              onChange={(e) => set('tipo_documento_vendita', e.target.value as TipoDocumentoVendita)}
            >
              <option value="TD01">{t('settings.dati_fatt.td_td01')}</option>
              <option value="TD24">{t('settings.dati_fatt.td_td24')}</option>
            </select>
            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
              <AlertTriangle size={13} /> {t('settings.dati_fatt.tipo_documento_hint')}
            </p>
          </div>
        </div>
      </div>

      {/* V69 — Ricezione fatture dai fornitori: sezione SEPARATA dal blocco
          cedente. I nomi si somigliano (codice_trasmittente, pec_cedente) ma i
          concetti sono tre e diversi: l'help text esiste per questo. Questi due
          campi alimentano SOLO il prospetto passivo, mai gli XML emessi. */}
      <div className="pt-3 border-t border-stone-mid/60">
        <h3 className="text-sm font-semibold text-ink-dark">{t('settings.dati_fatt.ricezione_titolo')}</h3>
        <p className="text-xs text-ink-soft mt-1 mb-3">{t('settings.dati_fatt.ricezione_help')}</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>{t('settings.dati_fatt.codice_dest_ricezione')}</label>
            <input
              type="text" maxLength={7} className={inputCls}
              data-testid="codice-dest-ricezione"
              value={form.codice_destinatario_ricezione}
              onChange={(e) => set('codice_destinatario_ricezione', e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>{t('settings.dati_fatt.pec_ricezione')}</label>
            <input
              type="text" className={inputCls}
              data-testid="pec-ricezione"
              value={form.pec_ricezione}
              onChange={(e) => set('pec_ricezione', e.target.value)}
            />
          </div>
        </div>
      </div>
      </fieldset>

      {canWrite && (
        <div className="flex justify-end pt-2">
          <button
            type="button" onClick={save} disabled={saving}
            data-testid="fatturazione-save"
            className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60"
          >
            {saving ? t('settings.dati_fatt.salvataggio') : t('settings.dati_fatt.salva')}
          </button>
        </div>
      )}
    </div>
  );
}
